import { type PersistedState } from './types';
import fastDeepEqual from 'fast-deep-equal';
import merge from 'deepmerge';
import { type RootState } from 'reducers/root';
import {
  type PartialDeep,
  type Schema,
  type PartialOnUndefinedDeep,
} from 'type-fest';

export function safeJsonParse(text: unknown): PersistedState {
  if (typeof text === 'string' && text) {
    try {
      return JSON.parse(text) as PersistedState;
    } catch {
      return {};
    }
  }

  return {};
}

// We don't want to deal with `undefined` because it's not serializable:
function stripUndefinedProperties<ObjectType>(
  object: ObjectType,
): PartialOnUndefinedDeep<ObjectType> {
  return JSON.parse(
    JSON.stringify(object),
  ) as PartialOnUndefinedDeep<ObjectType>;
}

// Strips undefined properties first, then compares:
export function isEqual(object1: unknown, object2: unknown): boolean {
  return fastDeepEqual(
    stripUndefinedProperties(object1),
    stripUndefinedProperties(object2),
  );
}

export function mergeDeepExceptArrays(
  currentState: RootState,
  persistedState: PersistedState,
): RootState {
  return merge<RootState, PersistedState>(
    currentState,
    persistedState,

    // Replace arrays:
    { arrayMerge: (destinationArray, sourceArray) => sourceArray },
  );
}

export function clearSmoochSessionUponNewPersistence(
  persistedState: PersistedState,
) {
  const integrationId =
    persistedState.liveChat?.integrationConfig?.integrationId;
  const integration = persistedState.liveChat?.integration;

  if (integrationId && integration === 'sunco') {
    clearSmoochSession(integrationId);
  }
}

export function clearSmoochSession(integrationId: string) {
  // `localStorage.removeItem()` can throw "SecurityError: The operation is
  // insecure" if user enabled blocking of cookies:
  try {
    localStorage.removeItem(`${integrationId}.sessionToken`);
    localStorage.removeItem(`${integrationId}.clientId`);
    localStorage.removeItem(`${integrationId}.appUserId`);
  } catch {
    // Continue regardless of error
  }
}

export function omit<
  ObjectType,
  OmitObjectType extends PartialDeep<Schema<ObjectType, undefined>>,
>(
  object: ObjectType,
  toOmit: OmitObjectType,
): PartialOnUndefinedDeep<PartialDeep<ObjectType>> {
  // deepmerge type is incorrect, uses shallow intersection instead of deep
  // merge:
  const merged = merge<ObjectType, OmitObjectType>(
    object,
    toOmit,
  ) as PartialDeep<ObjectType>;

  return stripUndefinedProperties(merged);
}
